import type { ISection } from '@/assets/types/api/Pages';
import type { IPartnersItem } from '@/assets/types/api/Partners';
import type { ISelectOption } from '@/components/Shared/Field/SelectCaption.vue';

export const usePartnersStore = defineStore('partnersStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const title = ref<string>('');
  const sections = ref<ISection[]>([]);
  const allPartners = ref<IPartnersItem[]>([]);
  const trustedPartners = ref<IPartnersItem[]>([]);

  /* Getters */

  const countriesList = computed((): ISelectOption[] => {
    const result: string[] = allPartners.value.map(({ country }) => country);
    const resultSet: Set<string> = new Set([...result]);
    const resultMap: ISelectOption[] = [...resultSet].map((item: string) => ({
      name: item,
      value: item,
    }));
    return [{ name: 'All Countries', value: '' }, ...resultMap];
  });

  const solutionsList = computed((): ISelectOption[] => {
    const result: string[] = allPartners.value.reduce(
      (prev: string[], current: IPartnersItem) => {
        const solutions: string[] = current.focusedSolutions.map(
          ({ name }) => name
        );
        return [...prev, ...solutions];
      },
      []
    );
    const resultSet: Set<string> = new Set([...result]);
    const resultMap: ISelectOption[] = [...resultSet].map((item: string) => ({
      name: item,
      value: item,
    }));
    return [{ name: 'All Solutions', value: '' }, ...resultMap];
  });

  /* Actions */

  const USE_GET_PARTNERS_PAGE = async () => {
    /**
     * 取得 Partners 頁面資訊
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () => `/marketplace/api/v1/pages/partners?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            const data = _data as { title: string; sections: ISection[] };
            title.value = data.title;
            sections.value = [...data.sections];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const USE_GET_TRUSTED_PARTNERS = async () => {
    /**
     * 取得值得信任的合作夥伴清單
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () => `/marketplace/api/v1/partners/trusted?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            trustedPartners.value = [...(_data as IPartnersItem[])];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const USE_GET_ALL_PARTNERS = async () => {
    /**
     * 取得所有合作夥伴資訊
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () => `/marketplace/api/v1/partners?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            allPartners.value = [...(_data as IPartnersItem[])];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  return {
    title,
    sections,
    allPartners,
    trustedPartners,
    countriesList,
    solutionsList,
    USE_GET_PARTNERS_PAGE,
    USE_GET_TRUSTED_PARTNERS,
    USE_GET_ALL_PARTNERS,
  };
});
